import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useOutlet } from 'react-router-dom';
import { DesignSystemSidebar } from './DesignSystemSidebar';

/** Duración del fade de transición entre páginas (ms). */
const FADE_MS = 150;

/**
 * Renderiza la página activa con una transición fade out → fade in al navegar.
 * Mantiene el outlet anterior en estado durante el fade-out y recién ahí lo cambia
 * por el nuevo (con `useOutlet` + `useRef` para tener siempre el último). Sin libs
 * extra. Ver ADR-680.
 */
const AnimatedOutlet: React.FC = () => {
  const outlet = useOutlet();
  const { pathname } = useLocation();
  // Ref al outlet más reciente: el timeout lo lee al hacer el swap.
  const outletRef = useRef(outlet);
  outletRef.current = outlet;

  const [displayed, setDisplayed] = useState<{ node: React.ReactNode; path: string }>({
    node: outlet,
    path: pathname,
  });
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    if (pathname === displayed.path) return;
    setVisible(false); // fade out del contenido viejo (displayed.node)
    const t = setTimeout(() => {
      setDisplayed({ node: outletRef.current, path: pathname }); // swap al nuevo
      setVisible(true); // fade in
    }, FADE_MS);
    return () => clearTimeout(t);
  }, [pathname, displayed.path]);

  return (
    <div style={{ opacity: visible ? 1 : 0, transition: `opacity ${FADE_MS}ms ease` }}>
      {displayed.node}
    </div>
  );
};

/**
 * Shell del Design System v3: sidebar de carpetas anidadas a la izquierda + área
 * de contenido a la derecha. El divider vertical sidebar ↔ contenido lo aporta el
 * `border-r` del sidebar.
 *
 * La clase `ds-root` habilita el cursor pointer nativo (manito) sobre los elementos
 * interactivos del DS, en vez del cursor custom de la app (ver `index.css`).
 */
export const DesignSystemLayout: React.FC = () => (
  <div className="ds-root flex h-screen w-full overflow-hidden bg-white text-neutral-900 antialiased">
    <DesignSystemSidebar />
    <main className="flex-1 overflow-y-auto">
      <AnimatedOutlet />
    </main>
  </div>
);
